import { useEffect, useRef, useCallback } from "react";

interface UsePollingOptions {
  callback: () => void;
  intervalMs: number;
  maxPolls?: number;
  onComplete?: () => void;
  immediate?: boolean;
}

/**
 * Hook for managing multiple intervals with automatic cleanup.
 *
 * Useful for coordinating related intervals (e.g., progress animation + status polling)
 * that should all be cleaned up together.
 *
 * Each instance manages its own independent set of intervals.
 */
export const usePolling = () => {
  const intervalsRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const isMountedRef = useRef(true);

  /**
   * Clears all intervals managed by this instance.
   * Called automatically on unmount.
   */
  const clear = useCallback(() => {
    for (const interval of Array.from(intervalsRef.current)) {
      clearInterval(interval);
    }
    intervalsRef.current.clear();
  }, []);

  /**
   * Creates and starts an interval that will be automatically cleaned up.
   *
   * @param options.callback - Function to run on each interval
   * @param options.intervalMs - Milliseconds between each execution
   * @param options.maxPolls - Optional: Stop after this many executions
   * @param options.onComplete - Optional: Called when maxPolls is reached
   * @param options.immediate - Optional: If true, call callback immediately before starting interval (default: false)
   */
  const start = useCallback(
    ({ callback, intervalMs, maxPolls, onComplete, immediate = false }: UsePollingOptions) => {
      let pollCount = 0;

      // Call immediately if requested
      if (immediate) {
        callback();
        pollCount++;

        // If maxPolls is 1, we're done
        if (maxPolls === 1) {
          onComplete?.();
          return undefined;
        }
      }

      const interval = globalThis.setInterval(() => {
        if (!isMountedRef.current) return;

        pollCount++;
        callback();

        if (maxPolls && pollCount >= maxPolls) {
          clearInterval(interval);
          intervalsRef.current.delete(interval);
          onComplete?.();
        }
      }, intervalMs);

      intervalsRef.current.add(interval);
      return interval;
    },
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clear();
    };
  }, [clear]);

  return { start, clear };
};
